import CloseOutlined from '@ant-design/icons/CloseOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import FolderOutlined from '@ant-design/icons/FolderOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SourceChip } from './useSourceChips';
import styles from './composer.module.css';

/**
 * Selected guidelines & context as chips (spec §3). Deleted sources are
 * struck through; in Chat only they stay selected but are labelled unused,
 * because nothing is sent.
 */
const SourceChips = ({
  chips,
  chatOnly,
  removing,
  failed,
  busy = false,
  onRemove,
}: {
  chips: SourceChip[];
  chatOnly: boolean;
  removing: string | null;
  failed: boolean;
  /** The selection is still being resolved: removing must wait. */
  busy?: boolean;
  onRemove: (key: string) => void;
}) => {
  const { t } = useTranslation();
  if (chips.length === 0) return null;
  return (
    <div className={styles.chipsWrap}>
      <ul
        className={styles.chips}
        aria-label={t('v2.composer.chipsLabel')}
        data-testid="source-chips"
        data-unused={chatOnly || undefined}
      >
        {chips.map(chip => {
          const name =
            chip.name ?? t('v2.composer.unnamedSource', { id: chip.id });
          const label = [
            name,
            chip.removed ? t('v2.composer.ex_removed') : null,
            chatOnly ? t('v2.composer.notUsedChat') : null,
          ]
            .filter(Boolean)
            .join(t('v2.common.listSeparator'));
          return (
            <li
              key={chip.key}
              className={styles.chip}
              data-kind={chip.kind}
              data-removed={chip.removed || undefined}
              data-unused={chatOnly || undefined}
              data-testid="source-chip"
            >
              {chip.kind === 'folder' ? (
                <FolderOutlined aria-hidden />
              ) : (
                <FileTextOutlined aria-hidden />
              )}
              <Tooltip title={label}>
                <span className={styles.chipName}>{name}</span>
              </Tooltip>
              {chip.removed ? (
                <span className={styles.chipNote}>
                  {t('v2.common.paren', { text: t('v2.composer.ex_removed') })}
                </span>
              ) : null}
              {chatOnly ? (
                <span className={styles.chipNote}>
                  {t('v2.common.paren', { text: t('v2.composer.notUsedChat') })}
                </span>
              ) : null}
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={t('v2.common.removeNamed', { name })}
                title={t('v2.composer.removeRef')}
                disabled={removing !== null || busy}
                onClick={() => onRemove(chip.key)}
                data-testid="source-chip-remove"
              >
                {removing === chip.key ? (
                  <LoadingOutlined aria-hidden />
                ) : (
                  <CloseOutlined aria-hidden />
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {failed ? (
        <p className={styles.inlineError} role="alert">
          {t('v2.composer.chipRemoveFailed')}
        </p>
      ) : null}
    </div>
  );
};

export default SourceChips;
